<template>
  <div :class="{ 'sm:col-span-2': field.wide }">
    <template v-if="field.type === 'toggle'">
      <label class="flex cursor-pointer items-center gap-3">
        <button
          type="button"
          role="switch"
          :aria-checked="!!modelValue"
          :disabled="disabled"
          class="relative h-6 w-11 shrink-0 rounded-full bg-slate-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 disabled:opacity-50 dark:bg-slate-700"
          :class="{ 'gradient-brand': modelValue }"
          @click="$emit('update:modelValue', !modelValue)"
        >
          <span class="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all" :class="modelValue ? 'left-[22px]' : 'left-0.5'" />
        </button>
        <span class="text-sm font-semibold text-slate-700 dark:text-slate-300">{{ field.label }}</span>
      </label>
    </template>

    <template v-else>
      <label :for="id" class="label">
        {{ field.label }}<span v-if="field.required" class="ml-0.5 text-rose-500" aria-hidden="true">*</span>
      </label>

      <LookupSelect
        v-if="field.type === 'lookup'"
        :id="id"
        :model-value="modelValue"
        :lookup="field.lookup"
        :only-where="field.onlyWhere"
        :blank="field.blank"
        :required="field.required"
        :placeholder="field.placeholder"
        :disabled="disabled"
        :invalid="!!errors.length"
        @update:model-value="$emit('update:modelValue', $event)"
      />

      <LookupSelect
        v-else-if="field.type === 'select'"
        :id="id"
        :model-value="modelValue"
        :static-options="resolvedOptions"
        :blank="field.blank"
        :required="field.required"
        :disabled="disabled"
        :invalid="!!errors.length"
        @update:model-value="$emit('update:modelValue', $event)"
      />

      <div v-else-if="field.type === 'segmented'" class="flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-900" role="radiogroup" :aria-label="field.label">
        <button
          v-for="o in resolvedOptions"
          :key="o.value"
          type="button"
          role="radio"
          :aria-checked="modelValue === o.value"
          :disabled="disabled"
          class="flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors"
          :class="modelValue === o.value ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'"
          @click="$emit('update:modelValue', o.value)"
        >
          {{ o.label }}
        </button>
      </div>

      <textarea
        v-else-if="field.type === 'textarea'"
        :id="id"
        rows="3"
        class="input resize-y"
        :class="{ 'input-error': errors.length }"
        :maxlength="field.max"
        :placeholder="field.placeholder"
        :disabled="disabled"
        :value="modelValue"
        @input="$emit('update:modelValue', $event.target.value)"
      />

      <div v-else-if="field.type === 'money'" class="relative">
        <input
          :id="id"
          type="text"
          inputmode="decimal"
          autocomplete="off"
          class="input pr-16 font-semibold"
          :class="{ 'input-error': errors.length }"
          :placeholder="field.placeholder || '0'"
          :disabled="disabled"
          :value="modelValue"
          @input="$emit('update:modelValue', $event.target.value)"
        />
        <span v-if="currency" class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 dark:text-slate-400">{{ currency }}</span>
      </div>

      <div v-else-if="field.type === 'password'">
        <input
          :id="id"
          :type="revealed ? 'text' : 'password'"
          class="input"
          :class="{ 'input-error': errors.length, 'font-mono': revealed }"
          :autocomplete="field.autocomplete"
          :placeholder="field.placeholder"
          :disabled="disabled"
          :value="modelValue"
          @input="$emit('update:modelValue', $event.target.value)"
        />
        <!-- Générer / copier : le mot de passe n'est jamais envoyé par e-mail, l'administrateur le communique. -->
        <div v-if="field.generate" class="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold">
          <button type="button" class="text-cyan-700 hover:underline disabled:opacity-40 dark:text-cyan-300" :disabled="disabled" @click="generate">Générer un mot de passe</button>
          <button type="button" class="text-cyan-700 hover:underline disabled:opacity-40 dark:text-cyan-300" :disabled="!modelValue" @click="copy">Copier</button>
          <button type="button" class="text-slate-500 hover:underline disabled:opacity-40 dark:text-slate-400" :disabled="!modelValue" @click="revealed = !revealed">{{ revealed ? 'Masquer' : 'Afficher' }}</button>
        </div>
      </div>

      <div v-else-if="field.type === 'file'">
        <input
          :id="id"
          type="file"
          :accept="field.accept"
          :disabled="disabled"
          class="block w-full cursor-pointer rounded-xl border border-dashed border-slate-300 bg-slate-50 p-2.5 text-sm text-slate-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white dark:file:bg-slate-700"
          @change="$emit('update:modelValue', $event.target.files[0] || null)"
        />
      </div>

      <div v-else class="relative">
        <input
          :id="id"
          :type="inputType"
          class="input"
          :class="{ 'input-error': errors.length, 'pr-16': field.suffix }"
          :placeholder="field.placeholder"
          :autocomplete="field.autocomplete"
          :maxlength="field.max"
          :min="inputMin"
          :max="inputMax"
          :step="field.step"
          :disabled="disabled"
          :value="modelValue"
          @input="onInput"
        />
        <span v-if="field.suffix" class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 dark:text-slate-400">{{ field.suffix }}</span>
      </div>
    </template>

    <p v-if="field.hint && !errors.length" class="mt-1 text-xs text-slate-400 dark:text-slate-500">{{ field.hint }}</p>
    <p v-if="field.notice" class="mt-1.5 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs leading-relaxed text-slate-500 dark:bg-slate-900 dark:text-slate-400">{{ field.notice }}</p>
    <p v-for="(message, i) in errors" :key="i" class="mt-1 text-xs font-medium text-rose-600 dark:text-rose-400" role="alert">{{ message }}</p>
  </div>
</template>

<script>
import LookupSelect from './LookupSelect.vue'
import { session } from '@/stores/session'
import { toast } from '@/stores/toast'
import { copyText, generatePassword } from '@/utils/password'
import { currencySymbol } from '@/utils/money'

let uid = 0

export default {
  name: 'FormField',
  components: { LookupSelect },
  props: {
    /** Description du champ : voir config/*.js (name, label, type, required…). */
    field: { type: Object, required: true },
    modelValue: { type: [String, Number, Boolean, File], default: '' },
    errors: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    form: { type: Object, default: () => ({}) }
  },
  emits: ['update:modelValue', 'generated'],
  data() {
    return { id: `f${++uid}-${this.field.name}`, revealed: false }
  },
  computed: {
    currency() {
      return currencySymbol(session.currency)
    },
    resolvedOptions() {
      const options = typeof this.field.options === 'function' ? this.field.options(this.form) : this.field.options
      return options || []
    },
    inputType() {
      const map = { integer: 'number', number: 'number', datetime: 'datetime-local' }
      return map[this.field.type] || this.field.type || 'text'
    },
    inputMin() {
      if (this.field.type === 'integer' && this.field.min === undefined) return 0
      return this.field.min
    },
    inputMax() {
      // Une date métier ne peut pas dépasser demain (fuseau de l'organisation).
      if (this.field.type === 'date' && this.field.business) return session.maxBusinessDate()
      return this.field.maxValue
    }
  },
  methods: {
    generate() {
      const value = generatePassword()
      this.revealed = true
      this.$emit('update:modelValue', value)
      this.$emit('generated', value) // ex. remplir aussi la confirmation
    },
    async copy() {
      if (await copyText(String(this.modelValue))) toast.success('Mot de passe copié.')
      else toast.error('Copie impossible : sélectionnez le mot de passe et copiez-le à la main.')
    },
    onInput(event) {
      this.$emit('update:modelValue', event.target.value)
    }
  }
}
</script>
